import { BlockFunctionConstants } from './constants';
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useSelector } from 'react-redux';
import { AbsolutePositioned } from './helper/Layout';
import classNames from '../helper/classNames';
import { sendEvent } from '../helper/api';
import {
  EDIT_SCRIPT_TOOLIP_ID,
  EditScriptTooltip,
} from '../components/EditScriptToolTip';
import { ExceededScriptLimitsTooltip } from '../components/ExceededScriptLimitsToolTip';
import {
  SCRIPT_SEGMENTS_TOOLTIP_ID,
  ScriptSegmentsTooltip,
} from '../components/ScriptSegmentsTooltip';
import { pulseAnimation } from '../components/RecordingTooltip';
import { CheckBox } from '../components/common/CheckBox';
import { useTrackActivity } from '../hooks/track';

export const MAX_SEGMENTS = 40;
export const MAX_CHARACTERS = 4000;

export const BlockBuilderFunctions = {
  [BlockFunctionConstants.hasSummaryContent]: () => false,
};

function getAutoSegmentedScript(text, minSec = 5, maxSec = 15, wps = 3.33) {
  const minWords = Math.floor(minSec * wps);
  const maxWords = Math.floor(maxSec * wps);

  // Split on double newlines first to preserve old segments
  const hardBlocks = text.split(/\n{2,}/).filter(Boolean);

  const allSegments = [];

  for (const block of hardBlocks) {
    const sentences = block
      .split(/(?<=[.?!])\s+|\n+/) // split at punctuation or newline
      .filter(Boolean);

    let buffer = [];
    let wordCount = 0;

    for (const sentence of sentences) {
      const wordsInSentence = sentence.trim().split(/\s+/).length;

      if (wordCount + wordsInSentence <= maxWords) {
        buffer.push(sentence.trim());
        wordCount += wordsInSentence;
      } else {
        if (wordCount >= minWords) {
          allSegments.push(buffer.join(' '));
          buffer = [sentence.trim()];
          wordCount = wordsInSentence;
        } else {
          buffer.push(sentence.trim());
          wordCount += wordsInSentence;
        }
      }
    }

    if (buffer.length > 0) {
      allSegments.push(buffer.join(' '));
    }
  }

  return allSegments.join('\n\n');
}

const Block = ({ block, user, eventDispatch, isAuthoring }) => {
  const { position, state, settings } = block || {};
  const {
    script: blockStateScript,
    autoSegmentScript: blockStateAutoSegmentScript,
  } = state || {};
  const { script = '' } = settings || {};
  const [isEditableMode, setIsEditableMode] = useState(false);
  const [localText, setLocalText] = useState(blockStateScript);
  const [autoSegmentScript, setAutoSegmentScript] = useState(
    blockStateAutoSegmentScript
  );
  const latestLocalText = useRef(localText);
  const latestAutoSegmentScript = useRef(autoSegmentScript);
  const userId = useSelector((st) => st.auth?.user?.userId);
  const authoringSegments = script.split('\n\n').filter((s) => s.length !== 0);
  const meetingId = useSelector((state) => state.meetingState.meetingId);
  const meetingStateScript = useSelector(
    (state) => state.meetingState.state?.script
  );
  const segments = (isAuthoring ? script : meetingStateScript || localText)
    .split('\n\n')
    .filter((s) => s.length !== 0);
  const isInitialized = useRef(false);
  const wentLive = useSelector((state) => state.meetingState.state?.wentLive);

  const [localShowEditScriptTooltip, setLocalShowEditScriptTooltip] =
    useState(true);
  const [
    localShowExceedScriptLimitsTooltip,
    setLocalShowExceedScriptLimitsTooltip,
  ] = useState(false);
  const [localShowScriptSegmentsTooltip, setLocalShowScriptSegmentsTooltip] =
    useState(false);

  const track = useTrackActivity();

  const handleDismissTooltip = useCallback((id, setLocalShowTooltip) => {
    if (id && !!!localStorage.getItem(`tooltip-${id}-dismissed`)) {
      localStorage.setItem(`tooltip-${id}-dismissed`, true);
    }
    setLocalShowTooltip && setLocalShowTooltip(false);
  }, []);

  const handleUpdateVideoScript = useCallback(
    async (text) => {
      track('VideoScriptEditorBlock: Video Script Updated');

      let usedText = text ? text : localText;
      let segmentedScript = autoSegmentScript
        ? getAutoSegmentedScript(usedText)
        : usedText;

      const updateVideoScriptAction = {
        type: 'UPDATE_VIDEO_SCRIPT',
        userId,
        data: { newScript: segmentedScript },
      };

      await sendEvent(userId, meetingId, updateVideoScriptAction);
      eventDispatch(updateVideoScriptAction);
    },
    [track, autoSegmentScript, eventDispatch, localText, meetingId, userId]
  );

  const handleUpdateAutoSegmentScript = useCallback(
    async (value) => {
      track('VideoScriptEditorBlock: User updated auto segment script', {
        autoSegmentScript: value,
      });

      const updateAutoSegmentScriptAction = {
        type: 'UPDATE_AUTO_SEGMENT_SCRIPT',
        userId,
        data: { value },
      };

      eventDispatch(updateAutoSegmentScriptAction);
      setAutoSegmentScript(value);
    },
    [eventDispatch, userId, track]
  );

  const handleKeyDown = useCallback(
    (e) => {
      if (localShowExceedScriptLimitsTooltip) return;
      if (
        (localText?.length >= MAX_CHARACTERS ||
          (autoSegmentScript &&
            getAutoSegmentedScript(localText || '').split(/\n\n/).length >
              MAX_SEGMENTS) ||
          (!autoSegmentScript &&
            (localText || '').split(/\n\n/).length > MAX_SEGMENTS)) &&
        ![
          'Backspace',
          'Delete',
          'ArrowLeft',
          'ArrowRight',
          'ArrowUp',
          'ArrowDown',
        ].includes(e.key)
      ) {
        setLocalShowExceedScriptLimitsTooltip(true);
      }
    },
    [autoSegmentScript, localShowExceedScriptLimitsTooltip, localText]
  );

  const handlePaste = useCallback(
    (e) => {
      if (localShowExceedScriptLimitsTooltip) return;

      const paste = e.clipboardData.getData('text');
      const textarea = e.target;

      const isAllSelected =
        textarea.selectionStart === 0 &&
        textarea.selectionEnd === textarea.value.length;

      const newText = isAllSelected
        ? paste
        : localText.slice(0, textarea.selectionStart) +
          paste +
          localText.slice(textarea.selectionEnd);

      const exceedsCharLimit = newText.length > MAX_CHARACTERS;
      const segmentCount = (
        autoSegmentScript
          ? getAutoSegmentedScript(newText || '')
          : newText || ''
      ).split(/\n\n/).length;
      const exceedsSegmentLimit = segmentCount > MAX_SEGMENTS;

      if (exceedsCharLimit || exceedsSegmentLimit) {
        e.preventDefault();
        setLocalShowExceedScriptLimitsTooltip(true);
      }
    },
    [autoSegmentScript, localShowExceedScriptLimitsTooltip, localText]
  );

  useEffect(() => {
    if (isAuthoring) return;
    if (isInitialized.current) return;
    if (meetingStateScript === blockStateScript) return;
    isInitialized.current = true;
    const setVideoScriptInMeetingState = async () => {
      handleUpdateVideoScript();
    };

    setVideoScriptInMeetingState();
  }, [
    blockStateScript,
    handleUpdateVideoScript,
    isAuthoring,
    meetingStateScript,
  ]);

  useEffect(() => {
    latestLocalText.current = localText;
  }, [localText]);

  useEffect(() => {
    latestAutoSegmentScript.current = autoSegmentScript;
  }, [autoSegmentScript]);

  useEffect(() => {
    return () => {
      if (isAuthoring) return;
      handleUpdateVideoScript(
        latestAutoSegmentScript.current
          ? getAutoSegmentedScript(latestLocalText.current)
          : latestLocalText.current
      );
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (localStorage.getItem(`tooltip-${EDIT_SCRIPT_TOOLIP_ID}-dismissed`)) {
      setLocalShowEditScriptTooltip(false);
    }
  }, []);
  const lines = (localText || '').split(/\n\n/);

  return (
    <AbsolutePositioned position={position} className="flex flex-col gap-9">
      <style>{pulseAnimation}</style>
      <div className="flex gap-[5px] justify-end h-[40px]">
        {isEditableMode ? (
          <>
            <CheckBox
              label="Auto-Segment Script"
              value={autoSegmentScript}
              onChange={(value) => handleUpdateAutoSegmentScript(value)}
              labelClassName="!text-white"
              checkboxClassName="!focus:ring-transparent"
            />
            <button
              className="w-[86px] h-[40px] font-montserrat bg-[#EA4748] rounded-xl font-bold text-white"
              onClick={() => {
                setLocalText(blockStateScript);
                setIsEditableMode(false);
              }}
            >
              Cancel
            </button>
            <button
              className="w-[86px] h-[40px] font-montserrat bg-[#0C89FF] rounded-xl font-bold text-white disabled:opacity-50"
              onClick={async () => {
                await handleUpdateVideoScript();
                setIsEditableMode(false);
              }}
              disabled={
                localText?.length > MAX_CHARACTERS ||
                lines.length > MAX_SEGMENTS
              }
            >
              Save
            </button>
          </>
        ) : (
          <div className="relative flex w-fit h-fit">
            {localShowEditScriptTooltip && (
              <EditScriptTooltip
                setLocalShowEditScriptTooltip={setLocalShowEditScriptTooltip}
                handleDismissTooltip={handleDismissTooltip}
                style={{ right: '-70px' }}
              >
                <div className="hidden"></div>
              </EditScriptTooltip>
            )}
            <button
              className={classNames(
                'w-[92px] h-[40px] font-montserrat  rounded-xl text-white font-bold ',
                wentLive ? 'bg-[#525252] opacity-25' : 'bg-[#0C89FF]'
              )}
              onClick={() => {
                if (
                  !localShowScriptSegmentsTooltip &&
                  !!!localStorage.getItem(
                    `tooltip-${SCRIPT_SEGMENTS_TOOLTIP_ID}-dismissed`
                  )
                ) {
                  setLocalShowScriptSegmentsTooltip(true);
                }
                setIsEditableMode(true);
              }}
              disabled={wentLive}
            >
              Edit
            </button>
          </div>
        )}
      </div>
      <div
        className={classNames(
          'flex rounded-[25px] py-[26px] w-full relative',
          !isEditableMode &&
            'border border-[#FFFFFF40] bg-[#363636] h-[calc(100%-50px)]',
          isEditableMode && 'bg-white h-full'
        )}
      >
        {localShowExceedScriptLimitsTooltip && (
          <ExceededScriptLimitsTooltip
            handleDismissTooltip={handleDismissTooltip}
            setLocalShowExceedScriptLimitsTooltip={
              setLocalShowExceedScriptLimitsTooltip
            }
          >
            <div className="hidden"></div>
          </ExceededScriptLimitsTooltip>
        )}
        {localShowScriptSegmentsTooltip && (
          <ScriptSegmentsTooltip
            setLocalShowScriptSegmentsTooltip={
              setLocalShowScriptSegmentsTooltip
            }
            handleDismissTooltip={handleDismissTooltip}
          >
            <div className="hidden"></div>
          </ScriptSegmentsTooltip>
        )}
        {isEditableMode ? (
          <div className="w-full">
            <textarea
              className="bg-white resize-none outline-none w-full h-full px-[26px] video-script-scrollbar font-roboto"
              maxLength={MAX_CHARACTERS}
              defaultValue={blockStateScript}
              onChange={(e) => {
                setLocalText(e.target.value);
              }}
              onKeyDown={handleKeyDown}
              onPaste={handlePaste}
            />
          </div>
        ) : (
          <div className="max-w-full max-h-full flex flex-col gap-2 overflow-y-auto video-script-scrollbar px-[26px] w-full">
            {isAuthoring
              ? authoringSegments.map((s, index) => (
                  <div
                    className="py-4 px-[25px] border border-[#FFFFFF40] rounded-[10px] font-roboto text-[#D0D0D0] w-full"
                    key={`video-script-editor-segment-${index}`}
                  >
                    {s}
                  </div>
                ))
              : segments.map((s, index) => (
                  <div
                    className="py-4 px-[25px] border border-[#FFFFFF40] rounded-[10px] font-roboto text-[#D0D0D0] w-full"
                    key={`video-script-editor-segment-${index}`}
                  >
                    {s}
                  </div>
                ))}
          </div>
        )}
      </div>
    </AbsolutePositioned>
  );
};

const AuthoringBlock = (props) => {
  return <Block {...props} isAuthoring />;
};

export { Block, AuthoringBlock };
